import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import fontsData from '../../data/fonts.json';
import GuideSeo from '../../components/Seo/GuideSeo';
import './FontsPage.css';
import '../guides/GuidePage.css';
const FontsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('timefonts');
    const [currentTime, setCurrentTime] = useState('');
    const sectionsRef = useRef({});
    // Update current time every second
    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            setCurrentTime(`${hours}:${minutes}:${seconds}`);
        };
        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, []);
    // Scroll spy for tabs
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            const viewportHeight = window.innerHeight;
            let currentSection = 'timefonts';
            let maxVisible = 0;
            ['timefonts', 'datafonts'].forEach((section) => {
                const element = sectionsRef.current[section];
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const elementTop = rect.top + scrollPosition;
                    const elementBottom = elementTop + rect.height;
                    const visibleTop = Math.max(scrollPosition + 100, elementTop);
                    const visibleBottom = Math.min(scrollPosition + viewportHeight, elementBottom);
                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                    if (visibleHeight > maxVisible && rect.top < viewportHeight * 0.5) {
                        maxVisible = visibleHeight;
                        currentSection = section;
                    }
                }
            });
            setActiveTab(currentSection);
        };
        let ticking = false;
        const throttledScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener('scroll', throttledScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', throttledScroll);
    }, []);
    const handleTabClick = (tab) => {
        const element = sectionsRef.current[tab];
        if (element) {
            const rect = element.getBoundingClientRect();
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const offsetTop = scrollTop + rect.top - 100;
            window.scrollTo({
                top: offsetTop,
                behavior: 'smooth'
            });
            setActiveTab(tab);
        }
    };
    const fonts = fontsData;
    const currentFonts = fonts[activeTab]?.fonts || [];
    const getFontPath = (filename, category) => {
        const root = fonts[category].rootfolder;
        // Lokal (Dev-Server): Fonts liegen direkt unter /fonts/...
        // Produktion: Fonts liegen unter /assets/fonts/... → Prefix hinzufügen
        const isProd = process.env.NODE_ENV === 'production';
        const prefix = isProd ? '/assets' : '';
        return `${prefix}${root}${filename}`;
    };
    const getFontFamily = (font, category) => {
        // Create a safe CSS font-family name from the font filename
        const fontName = font.filename.replace(/\.(ttf|otf)$/i, '').replace(/[^a-zA-Z0-9]/g, '');
        return `font-${fontName}`;
    };
    return (_jsxs("div", { className: "fonts-page guide-page", children: [_jsx(GuideSeo, { route: "/guides/fonts", fallbackTitle: "Schriften / Fonts f\u00FCr Garmin Watchfaces", fallbackDescription: "\u00DCbersicht der verf\u00FCgbaren Uhrzeit- und Datenschriften (Fonts) f\u00FCr TitanicTurtle Garmin Watchfaces." }), _jsxs("div", { className: "guide-container container", children: [_jsx("div", { className: "guide-back", children: _jsxs("button", { type: "button", className: "guide-back-link", onClick: () => {
                                if (window.history.length > 1)
                                    navigate(-1);
                                else
                                    navigate('/help');
                            }, children: ["\u2190 ", t('guides.backToHelp', 'Back')] }) }), _jsx(motion.h1, { className: "page-title guide-title", initial: { opacity: 0, y: -20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 }, children: t('fonts.title', 'Schriftarten') }), _jsx("div", { className: "fonts-tabs-nav-fixed", children: _jsxs("div", { className: "fonts-tabs-nav-wrapper", children: [_jsxs("button", { className: `fonts-tab-button ${activeTab === 'timefonts' ? 'active' : ''}`, onClick: () => handleTabClick('timefonts'), children: [_jsx("span", { className: "fonts-tab-icon", children: "\uD83D\uDD50" }), _jsx("span", { className: "fonts-tab-label", children: t('fonts.timefonts', 'Zeitschriften') })] }), _jsxs("button", { className: `fonts-tab-button ${activeTab === 'datafonts' ? 'active' : ''}`, onClick: () => handleTabClick('datafonts'), children: [_jsx("span", { className: "fonts-tab-icon", children: "\uD83D\uDCCA" }), _jsx("span", { className: "fonts-tab-label", children: t('fonts.datafonts', 'Daten-Schriften') })] })] }) }), _jsxs("div", { className: "fonts-content-scroll", children: [_jsxs("div", { id: "section-timefonts", ref: (el) => (sectionsRef.current['timefonts'] = el), className: "fonts-content-section", children: [_jsx("h2", { className: "fonts-section-title", children: t('fonts.timefonts', 'Zeitschriften') }), _jsx("div", { className: "fonts-grid", children: fonts.timefonts.fonts.map((font, index) => (_jsxs(motion.div, { className: "font-card", initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { delay: index * 0.05, duration: 0.5 }, children: [_jsxs("div", { className: "font-preview-container", children: [_jsx("div", { className: "font-preview-time", style: {
                                                                fontFamily: `'${getFontFamily(font, 'timefonts')}', sans-serif`,
                                                                fontSize: `${Math.max(48, font.scaleH * 0.7)}px`,
                                                                transform: `scale(${font.ratio})`
                                                            }, children: currentTime }), _jsx("div", { className: "font-preview-numbers", style: {
                                                                fontFamily: `'${getFontFamily(font, 'timefonts')}', sans-serif`,
                                                                fontSize: `${Math.max(36, font.scaleH * 0.5)}px`,
                                                                transform: `scale(${font.ratio})`
                                                            }, children: "0123456789:" })] }), _jsx("div", { className: "font-info", children: _jsx("h3", { className: "font-name", children: font.name }) }), _jsx("style", { children: `
                    @font-face {
                      font-family: '${getFontFamily(font, 'timefonts')}';
                      src: url('${getFontPath(font.filename, 'timefonts')}') format('truetype');
                      font-weight: normal;
                      font-style: normal;
                    }
                  ` })] }, font.font_id))) })] }), _jsxs("div", { id: "section-datafonts", ref: (el) => (sectionsRef.current['datafonts'] = el), className: "fonts-content-section", children: [_jsx("h2", { className: "fonts-section-title", children: t('fonts.datafonts', 'Daten-Schriften') }), _jsx("div", { className: "fonts-grid", children: fonts.datafonts.fonts.map((font, index) => (_jsxs(motion.div, { className: "font-card", initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { delay: index * 0.05, duration: 0.5 }, children: [_jsxs("div", { className: "font-preview-container", children: [_jsx("div", { className: "font-preview-time", style: {
                                                                fontFamily: `'${getFontFamily(font, 'datafonts')}', sans-serif`,
                                                                fontSize: `${Math.max(48, font.scaleH * 0.7)}px`,
                                                                transform: `scale(${font.ratio})`
                                                            }, children: "AaBbCcDdEeFf" }), _jsx("div", { className: "font-preview-numbers", style: {
                                                                fontFamily: `'${getFontFamily(font, 'datafonts')}', sans-serif`,
                                                                fontSize: `${Math.max(36, font.scaleH * 0.5)}px`,
                                                                transform: `scale(${font.ratio})`
                                                            }, children: "0123456789:" })] }), _jsx("div", { className: "font-info", children: _jsx("h3", { className: "font-name", children: font.name }) }), _jsx("style", { children: `
                    @font-face {
                      font-family: '${getFontFamily(font, 'datafonts')}';
                      src: url('${getFontPath(font.filename, 'datafonts')}') format('truetype');
                      font-weight: normal;
                      font-style: normal;
                    }
                  ` })] }, font.font_id))) })] })] })] })] }));
};
export default FontsPage;
